import { css } from '@styled-system/css';
import { Select as AntdSelect, Cascader } from 'antd';
import {
  Award,
  BadgeCheck,
  Euro,
  Globe,
  Hash,
  Layers,
  Package,
  Palette,
  Ruler,
  ShieldCheck,
  Shirt,
  Sparkles,
  X,
} from 'lucide-react';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChipMultiPicker } from '@/features/productSearch/components/ChipMultiPicker';
import { MobileCategoryDrilldown } from '@/features/productSearch/components/MobileCategoryDrilldown';
import { VestiaireUrlImport } from '@/features/productSearch/vestiaire/VestiaireUrlImport';
import { type CatalogNodeKey, parseCatalogNodeKey } from '@/features/productSearch/vestiaire/catalogTree';
import type {
  VestiaireAsyncFilterType,
  VestiaireSearchForm,
} from '@/features/productSearch/vestiaire/useVestiaireSearchForm';
import {
  VESTIAIRE_FULL_LIST_LIMIT,
  type VestiaireRegion,
  filledVestiaireSections,
  groupCountriesByRegion,
  toMaterialOptions,
} from '@/features/productSearch/vestiaire/vestiaireFilterOptions';
import { Accordion } from '@/shared/components/ui/Accordion';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { useGetVestiaireCountriesQuery, useGetVestiaireFilterOptionsQuery } from '@/shared/hooks/queries/generated';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import type { SelectOption } from '@/shared/types/options';
import { VESTIAIRE_ITEM_BADGES, VESTIAIRE_SELLER_BADGES } from '@/shared/utils/analysis/vestiaireFilterValues';

// ============================================================================
// Panneau de filtres Vestiaire Collective — même gabarit visuel que le panneau
// Vinted de SearchModal. Contrat : docs/superpowers/api-changelog/2026-09-28-analyses-vestiaire.md § 3.
// ============================================================================

type AntdTagRenderProps = { label: ReactNode; closable: boolean; onClose: () => void };

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 20;

const column = (gap: '3' | '4' | '5') => css({ display: 'flex', flexDirection: 'column', gap });

const errorText = css({ fontSize: 'caption', color: 'error.default' });

const regionLabel = css({
  fontSize: 'caption',
  fontWeight: 'semibold',
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

// Étiquettes des Select/Cascader : spans simples (mêmes jetons que le panneau Vinted) pour que
// le <Tag> d'AntD n'écrase pas les styles du design system.
const tagBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  marginInlineEnd: '2',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'sm',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontFamily: 'sans',
  fontSize: 'caption',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
} as const;

const categoryTag = css({ ...tagBase, bg: 'success.soft', color: 'success.default', borderColor: 'success.border' });
const brandTag = css({ ...tagBase, bg: 'info.soft', color: 'info.default', borderColor: 'info.border' });
const excludedTag = css({ ...tagBase, bg: 'error.soft', color: 'error.default', borderColor: 'error.border' });

const tagCloseBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.65,
  padding: 0,
  _hover: { opacity: 1 },
});

const useTagRender = (className: string) => {
  const { t } = useTranslation();
  return useCallback(
    ({ label, closable, onClose }: AntdTagRenderProps) => (
      <span className={className} onMouseDown={(e) => e.stopPropagation()}>
        {label}
        {closable && (
          <button type="button" onClick={onClose} className={tagCloseBtn} aria-label={t('searchModal.removeTag')}>
            <X size={10} strokeWidth={2.25} />
          </button>
        )}
      </span>
    ),
    [className, t],
  );
};

const isCatalogNodeKey = (value: unknown): value is CatalogNodeKey =>
  typeof value === 'string' && parseCatalogNodeKey(value) !== null;

const isCatalogNodePath = (path: readonly unknown[]): path is CatalogNodeKey[] => path.every(isCatalogNodeKey);

const toStrings = (ids: number[]) => ids.map(String);
const toNumbers = (values: string[]) => values.map(Number);

/** Liste complète sans recherche (état, couleur, matière), ordre serveur conservé. */
const useFullOptionList = (filterType: 'condition' | 'color' | 'material') =>
  useGetVestiaireFilterOptionsQuery(
    { filterType, query: null, limit: VESTIAIRE_FULL_LIST_LIMIT, ids: null },
    { staleTime: Infinity },
  ).data?.getVestiaireFilterOptions;

// ============================================================================
// Select asynchrone (marque, modèle, taille) : recherche serveur débouncée
// ============================================================================

interface AsyncFilterSelectProps {
  filterType: VestiaireAsyncFilterType;
  value: number[];
  onChange: (next: number[]) => void;
  placeholder: string;
  labels: Map<number, string>;
  rememberLabels: VestiaireSearchForm['rememberLabels'];
  /** Valeurs EXCLUES (marques exclues) : tags rouges, comme le panneau Vinted. */
  excluded?: boolean;
}

const AsyncFilterSelect: FC<AsyncFilterSelectProps> = ({
  filterType,
  value,
  onChange,
  placeholder,
  labels,
  rememberLabels,
  excluded = false,
}) => {
  const tagRender = useTagRender(excluded ? excludedTag : brandTag);
  const [results, setResults] = useState<{ id: number; title: string }[]>([]);
  const timer = useRef<number | null>(null);
  // Requête courante : une réponse arrivée pour une requête dépassée est ignorée.
  const currentQuery = useRef('');

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleSearch = (next: string) => {
    currentQuery.current = next;
    if (timer.current) window.clearTimeout(timer.current);
    if (next.trim().length < SEARCH_MIN_CHARS) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      try {
        const res = await useGetVestiaireFilterOptionsQuery.fetcher({
          filterType,
          query: next.trim(),
          limit: SEARCH_LIMIT,
          ids: null,
        })();
        if (currentQuery.current !== next) return;
        rememberLabels(filterType, res.getVestiaireFilterOptions);
        setResults(res.getVestiaireFilterOptions);
      } catch {
        // Liste vide plutôt que des options périmées (même choix que le panneau Vinted).
        if (currentQuery.current === next) setResults([]);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  // Union des ids sélectionnés (titre connu, sinon `#id`) et des résultats courants.
  const options = useMemo(() => {
    const selected = new Set(value);
    return [
      ...value.map((id) => ({ value: id, label: labels.get(id) ?? `#${id}` })),
      ...results
        .filter((result) => !selected.has(result.id))
        .map((result) => ({ value: result.id, label: result.title })),
    ];
  }, [value, labels, results]);

  return (
    <AntdSelect
      mode="multiple"
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={(next: number[]) => onChange(next)}
      showSearch
      filterOption={false}
      onSearch={handleSearch}
      notFoundContent={null}
      tagRender={tagRender}
      style={{ width: '100%' }}
    />
  );
};

// ============================================================================
// Panneau
// ============================================================================

export interface VestiaireFilterPanelProps {
  search: VestiaireSearchForm;
  readOnly?: boolean;
  showErrors?: boolean;
  /**
   * Section « Recherche par image » de SearchModal (même composant que l'onglet Vinted, état tenu
   * par la modale) — un `Accordion.Item` rendu dans l'accordéon du panneau.
   */
  similaritySection?: ReactNode;
  /** Sections à ouvrir (collage d'une image, édition d'une analyse par similarité) ; `seq` rejoue. */
  openRequest?: { sections: string[]; seq: number };
}

export const VestiaireFilterPanel: FC<VestiaireFilterPanelProps> = ({
  search,
  readOnly,
  showErrors,
  similaritySection,
  openRequest,
}) => {
  const { t } = useTranslation();
  const { form, setField, errors, treeOptions, treeLoading, labels, rememberLabels } = search;
  const isMobile = useIsMobile();
  const [openSections, setOpenSections] = useState<string[]>([]);
  // Lecture seule : ouvrir les sections remplies. Le préremplissage arrive en différé (arbre), d'où
  // l'union au fil des changements de `form` plutôt qu'un calcul unique au montage.
  const filledKey = readOnly ? filledVestiaireSections(form).join('|') : '';
  useEffect(() => {
    if (!filledKey) return;
    setOpenSections((previous) => Array.from(new Set([...previous, ...filledKey.split('|')])));
  }, [filledKey]);
  const openRequestSeq = openRequest?.seq ?? 0;
  const openRequestSections = openRequest?.sections;
  useEffect(() => {
    if (!openRequestSeq || !openRequestSections?.length) return;
    setOpenSections((previous) => Array.from(new Set([...previous, ...openRequestSections])));
    // Rejoué par `seq` seul : la liste peut être un nouvel objet de même contenu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequestSeq]);
  // Ouvre les sections en erreur à la première tentative d'envoi — comme Vinted (SearchModal.validate).
  useEffect(() => {
    if (!showErrors) return;
    const errorSections: string[] = [];
    if (errors.criteria) errorSections.push('categories');
    if (errors.priceMin) errorSections.push('price');
    if (errorSections.length > 0) {
      setOpenSections((previous) => Array.from(new Set([...previous, ...errorSections])));
    }
  }, [showErrors, errors.criteria, errors.priceMin]);
  const categoryTagRender = useTagRender(categoryTag);
  const excludedTagRender = useTagRender(excludedTag);

  const conditionOptions = useFullOptionList('condition');
  const colorOptions = useFullOptionList('color');
  const materialOptions = useFullOptionList('material');
  const { data: countriesData, isError: countriesError } = useGetVestiaireCountriesQuery(undefined, {
    staleTime: 60 * 60 * 1000,
  });

  const conditionChips = useMemo<SelectOption[]>(
    () => (conditionOptions ?? []).map((option) => ({ value: String(option.id), label: option.title })),
    [conditionOptions],
  );
  const colorChips = useMemo<SelectOption[]>(
    () => (colorOptions ?? []).map((option) => ({ value: String(option.id), label: option.title })),
    [colorOptions],
  );
  const materialChips = useMemo(() => toMaterialOptions(materialOptions ?? []), [materialOptions]);
  const countryGroups = useMemo(
    () => groupCountriesByRegion(countriesData?.getVestiaireCountries ?? []),
    [countriesData],
  );
  // Pays stockés absents de la liste servie (liste vide, en échec, ou pays retiré) : affichés en
  // pastille `#id` plutôt que retirés sans un mot. Pas pendant le chargement (flash de `#id`).
  const unknownCountryIds = useMemo(() => {
    if (!countriesData && !countriesError) return [];
    const known = new Set((countriesData?.getVestiaireCountries ?? []).map((country) => country.id));
    return form.countryIds.filter((id) => !known.has(id));
  }, [countriesData, countriesError, form.countryIds]);

  const regionLabels: Record<VestiaireRegion, string> = {
    europeWest: t('sellers.regions.europeWest'),
    unitedKingdom: t('sellers.regions.unitedKingdom'),
    europeEast: t('sellers.regions.europeEast'),
    northAmerica: t('sellers.regions.northAmerica'),
    others: t('sellers.regions.others'),
  };

  const toggleIn = <T,>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];

  const chipSection = (
    key: 'conditionIds' | 'colorIds' | 'materialIds',
    label: string,
    icon: ReactNode,
    options: SelectOption[],
  ) => (
    <Accordion.Item value={key}>
      <Accordion.Header icon={icon} label={label} count={form[key].length} />
      <Accordion.Content>
        <ChipMultiPicker
          options={options}
          value={toStrings(form[key])}
          onChange={(next) => setField(key, toNumbers(next))}
          searchPlaceholder={t('searchModal.filterSearchPlaceholder', { label: label.toLowerCase() })}
        />
      </Accordion.Content>
    </Accordion.Item>
  );

  // Même message et même emplacement que Vinted (SearchModal) : un seul texte pour min ≥ max et
  // pour une valeur invalide, affiché dans la section prix.
  const priceError = errors.priceMin ? t('searchModal.errorMinMax') : null;

  return (
    <div className={column('4')}>
      <div className={readOnly ? css({ pointerEvents: 'none', opacity: 0.65 }) : undefined}>
        <Accordion multiple value={openSections} onValueChange={setOpenSections}>
          {/* Import d'URL — création uniquement, comme Vinted */}
          {!readOnly && <VestiaireUrlImport search={search} />}

          {/* Catégories — clés `kind:id` (un id level2 peut égaler un id level1) */}
          <Accordion.Item value="categories">
            <Accordion.Header
              icon={<Shirt size={16} strokeWidth={1.75} />}
              label={t('vestiaireSearch.categories')}
              count={form.catalogPaths.length}
            />
            <Accordion.Content>
              <div className={column('3')}>
                {/* Mobile : même tiroir plein écran que Vinted (options génériques, chemins string[][]) */}
                {isMobile ? (
                  <MobileCategoryDrilldown
                    options={treeOptions}
                    value={form.catalogPaths}
                    onChange={(paths) => setField('catalogPaths', paths.filter(isCatalogNodePath))}
                    loading={treeLoading}
                    placeholder={t('vestiaireSearch.categoryPlaceholder')}
                  />
                ) : (
                  <Cascader
                    multiple
                    placeholder={t('vestiaireSearch.categoryPlaceholder')}
                    options={treeOptions}
                    value={form.catalogPaths}
                    onChange={(paths) => setField('catalogPaths', paths.filter(isCatalogNodePath))}
                    showCheckedStrategy={Cascader.SHOW_PARENT}
                    showSearch
                    maxTagCount="responsive"
                    expandTrigger="hover"
                    tagRender={categoryTagRender}
                    loading={treeLoading}
                    style={{ width: '100%' }}
                  />
                )}
                {/* Même message et emplacement que Vinted (SearchModal.errors.categories) */}
                {showErrors && errors.criteria && <span className={errorText}>{t('searchModal.errorScope')}</span>}
              </div>
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item value="brands">
            <Accordion.Header
              icon={<Award size={16} strokeWidth={1.75} />}
              label={t('vestiaireSearch.brands')}
              count={form.brandIds.length}
            />
            <Accordion.Content>
              <div className={css({ display: 'flex', flexDirection: 'column', gap: '4' })}>
                <AsyncFilterSelect
                  filterType="brand"
                  value={form.brandIds}
                  onChange={(next) => setField('brandIds', next)}
                  placeholder={t('vestiaireSearch.brandPlaceholder')}
                  labels={labels.brand}
                  rememberLabels={rememberLabels}
                />
                {/* Même libellés et même règle que Vinted : garde les articles sans marque connue et
                écarte les ids listés (excludedBrandIds) */}
                <FormField label={t('searchModal.excludeBrands')}>
                  <AsyncFilterSelect
                    filterType="brand"
                    value={form.excludedBrandIds}
                    onChange={(next) => setField('excludedBrandIds', next)}
                    placeholder={t('searchModal.excludeBrandPlaceholder')}
                    labels={labels.brand}
                    rememberLabels={rememberLabels}
                    excluded
                  />
                </FormField>
              </div>
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item value="models">
            <Accordion.Header
              icon={<Package size={16} strokeWidth={1.75} />}
              label={t('vestiaireSearch.models')}
              count={form.modelIds.length}
            />
            <Accordion.Content>
              <AsyncFilterSelect
                filterType="model"
                value={form.modelIds}
                onChange={(next) => setField('modelIds', next)}
                placeholder={t('vestiaireSearch.modelPlaceholder')}
                labels={labels.model}
                rememberLabels={rememberLabels}
              />
            </Accordion.Content>
          </Accordion.Item>

          {chipSection(
            'conditionIds',
            t('vestiaireSearch.condition'),
            <ShieldCheck size={16} strokeWidth={1.75} />,
            conditionChips,
          )}

          <Accordion.Item value="sizes">
            <Accordion.Header
              icon={<Ruler size={16} strokeWidth={1.75} />}
              label={t('vestiaireSearch.size')}
              count={form.sizeIds.length}
            />
            <Accordion.Content>
              <AsyncFilterSelect
                filterType="size"
                value={form.sizeIds}
                onChange={(next) => setField('sizeIds', next)}
                placeholder={t('vestiaireSearch.sizePlaceholder')}
                labels={labels.size}
                rememberLabels={rememberLabels}
              />
            </Accordion.Content>
          </Accordion.Item>

          {chipSection('colorIds', t('vestiaireSearch.color'), <Palette size={16} strokeWidth={1.75} />, colorChips)}
          {chipSection(
            'materialIds',
            t('vestiaireSearch.material'),
            <Layers size={16} strokeWidth={1.75} />,
            materialChips,
          )}

          {/* Pays du vendeur — ids ISO numériques : `name` rendu TEL QUEL, jamais la table Vinted */}
          {(countryGroups.length > 0 || form.countryIds.length > 0) && (
            <Accordion.Item value="countries">
              <Accordion.Header
                icon={<Globe size={16} strokeWidth={1.75} />}
                label={t('vestiaireSearch.country')}
                count={form.countryIds.length}
              />
              <Accordion.Content>
                <div className={column('5')}>
                  {countryGroups.map((group) => {
                    const groupIds = new Set(group.countries.map((country) => country.id));
                    // Chaque groupe ne voit (et ne vide) que ses propres pays.
                    const outside = form.countryIds.filter((id) => !groupIds.has(id));
                    return (
                      <div key={group.region} className={column('3')}>
                        <span className={regionLabel}>{regionLabels[group.region]}</span>
                        <ChipMultiPicker
                          options={group.countries.map((country) => ({
                            value: String(country.id),
                            label: country.name,
                          }))}
                          value={toStrings(form.countryIds.filter((id) => groupIds.has(id)))}
                          onChange={(next) => setField('countryIds', [...outside, ...toNumbers(next)])}
                        />
                      </div>
                    );
                  })}
                  {unknownCountryIds.length > 0 && (
                    // Pas d'intitulé propre (Vinted n'affiche rien pour un id sans groupe connu) :
                    // les pastilles `#id` restent visibles, sans en-tête de groupe.
                    <div className={column('3')}>
                      <ChipMultiPicker
                        options={unknownCountryIds.map((id) => ({ value: String(id), label: `#${id}` }))}
                        value={toStrings(unknownCountryIds)}
                        onChange={(next) =>
                          setField('countryIds', [
                            ...form.countryIds.filter((id) => !unknownCountryIds.includes(id)),
                            ...toNumbers(next),
                          ])
                        }
                      />
                    </div>
                  )}
                </div>
              </Accordion.Content>
            </Accordion.Item>
          )}

          {/* Recherche par image — même section que l'onglet Vinted (placée après les pays, comme là-bas) */}
          {similaritySection}

          <Accordion.Item value="sellerBadges">
            <Accordion.Header
              icon={<BadgeCheck size={16} strokeWidth={1.75} />}
              label={t('vestiaireSearch.seller')}
              count={form.sellerBadges.length}
            />
            <Accordion.Content>
              <div className={css({ display: 'flex', flexWrap: 'wrap', gap: '2' })}>
                {VESTIAIRE_SELLER_BADGES.map((badge) => (
                  <ChipToggle
                    key={badge}
                    selected={form.sellerBadges.includes(badge)}
                    onChange={() => setField('sellerBadges', toggleIn(form.sellerBadges, badge))}
                  >
                    {t(`vestiaireSearch.sellerBadge.${badge}`)}
                  </ChipToggle>
                ))}
              </div>
            </Accordion.Content>
          </Accordion.Item>

          {/* Badges d'annonce — flux vivant seulement (historique repris sans badge) */}
          <Accordion.Item value="itemBadges">
            <Accordion.Header
              icon={<Sparkles size={16} strokeWidth={1.75} />}
              label={t('vestiaireSearch.itemBadges')}
              count={form.itemBadges.length}
            />
            <Accordion.Content>
              <div className={column('3')}>
                <div className={css({ display: 'flex', flexWrap: 'wrap', gap: '2' })}>
                  {VESTIAIRE_ITEM_BADGES.map((badge) => (
                    <ChipToggle
                      key={badge}
                      selected={form.itemBadges.includes(badge)}
                      onChange={() => setField('itemBadges', toggleIn(form.itemBadges, badge))}
                    >
                      {t(`vestiaireSearch.itemBadge.${badge}`)}
                    </ChipToggle>
                  ))}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item value="price">
            <Accordion.Header
              icon={<Euro size={16} strokeWidth={1.75} />}
              label={t('segment.price')}
              count={(form.priceMin ? 1 : 0) + (form.priceMax ? 1 : 0)}
            />
            <Accordion.Content>
              <div className={column('3')}>
                <div className={css({ display: 'flex', gap: '3' })}>
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder={t('searchModal.min')}
                    value={form.priceMin}
                    onChange={(e) => setField('priceMin', e.target.value)}
                    trailing={<span className={css({ color: 'text.tertiary', fontSize: 'caption' })}>€</span>}
                  />
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder={t('searchModal.max')}
                    value={form.priceMax}
                    onChange={(e) => setField('priceMax', e.target.value)}
                    trailing={<span className={css({ color: 'text.tertiary', fontSize: 'caption' })}>€</span>}
                  />
                </div>
                {showErrors && priceError && <span className={errorText}>{priceError}</span>}
              </div>
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item value="keywords">
            <Accordion.Header
              icon={<Hash size={16} strokeWidth={1.75} />}
              label={t('searchModal.keywords')}
              count={form.keywords.length + form.excludedKeywords.length}
            />
            <Accordion.Content>
              <div className={column('4')}>
                <FormField label={t('searchModal.textSearch')} hint={t('vestiaireSearch.keywordsHint')}>
                  <AntdSelect
                    mode="tags"
                    placeholder={t('searchModal.keywordPlaceholder')}
                    tokenSeparators={[',']}
                    value={form.keywords}
                    onChange={(next: string[]) => setField('keywords', next)}
                    style={{ width: '100%' }}
                  />
                </FormField>

                {/* Opérateur d'inclusion — ET par défaut ; ne s'applique jamais aux mots exclus */}
                {form.keywords.length > 1 && (
                  <div className={css({ display: 'flex', gap: '2', alignItems: 'center' })}>
                    <ChipToggle
                      selected={form.keywordsOperator === 'and'}
                      onChange={() => setField('keywordsOperator', 'and')}
                    >
                      {t('searchModal.keywordsAnd')}
                    </ChipToggle>
                    <ChipToggle
                      selected={form.keywordsOperator === 'or'}
                      onChange={() => setField('keywordsOperator', 'or')}
                    >
                      {t('searchModal.keywordsOr')}
                    </ChipToggle>
                  </div>
                )}

                <FormField label={t('searchModal.excludedKeywords')} hint={t('searchModal.excludedKeywordsHint')}>
                  <AntdSelect
                    mode="tags"
                    placeholder={t('searchModal.excludedKeywordPlaceholder')}
                    tokenSeparators={[',']}
                    value={form.excludedKeywords}
                    onChange={(next: string[]) => setField('excludedKeywords', next)}
                    tagRender={excludedTagRender}
                    style={{ width: '100%' }}
                  />
                </FormField>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};
